import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";

/**
 * Set-your-password page for freshly provisioned admin-tier accounts
 * (franchise operator + franchise sub-admin). Mirrors the existing
 * vendor `/vendor/reset-password` UI so branding + copy + testids feel
 * consistent — the only difference is the backend endpoint we POST to.
 *
 * The token is a one-time value minted server-side at account
 * creation; hitting this page without a valid token shows a clear
 * "ask an admin to resend the invite" fallback rather than silently
 * failing.
 */
export default function AdminSetPasswordPage() {
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const tokenValid = useMemo(() => token.length >= 20, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!tokenValid) return toast.error("Link is missing its token.");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      await axios.post(`${backend}/api/auth/admin/set-password`, {
        token,
        new_password: pw,
      });
      toast.success("Password set. You can sign in now.");
      setDone(true);
      setTimeout(() => navigate("/admin/login", { replace: true }), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't set your password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EC] px-4 py-10 md:py-14 flex items-start justify-center">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <Link to="/" aria-label="City DOOH home" className="inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black rounded-md">
            <Logo size={150} light />
          </Link>
        </div>
        <Card className="p-6 md:p-7 border-2 border-[#0A0A0A]" data-testid="admin-set-password-card">
          <h1 className="font-heading text-2xl font-semibold text-center">Set your password</h1>
          <p className="text-sm text-muted-foreground text-center mt-1">
            One last step to activate your CityDooh admin account.
          </p>
          {!tokenValid ? (
            <div className="mt-6 rounded-lg border-2 border-destructive bg-destructive/10 p-4 text-sm text-destructive">
              This link is missing or malformed. Please ask an admin to resend your invite.
            </div>
          ) : done ? (
            <div className="mt-6 rounded-lg border-2 border-emerald-500 bg-emerald-50 p-4 text-sm text-emerald-700">
              All set — taking you to the sign-in page&hellip;
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <Input
                type="password"
                placeholder="New password (min 6 characters)"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoFocus
                data-testid="admin-set-password-new"
              />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                data-testid="admin-set-password-confirm"
              />
              <Button
                type="submit"
                className="w-full"
                disabled={busy || !pw || !pw2}
                data-testid="admin-set-password-submit"
              >
                {busy ? "Setting…" : "Set password & continue"}
              </Button>
              <p className="text-xs text-muted-foreground text-center pt-2">
                This link works once and expires in 72 hours from the time your invite was sent.
              </p>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
